'use client';

import React, { useState, memo, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import { signOut } from 'firebase/auth';
import { useRouter, usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetTrigger } from '@/components/ui/sheet';
import {
    Menu,
    LogOut,
    LogIn,
    UserPlus,
    LayoutDashboard,
    Settings,
    Loader2,
    Search,
    X,
    User as UserIcon,
    AlertCircle,
    ArchiveX,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useFirestore } from '@/hooks/useFirestore';
import { UserFirestoreData } from '@cigarsnap/shared';
import { navigationConfig, NavItemConfig } from '@/config/navigation';
import { cn } from '@/lib/utils';
import { appConfig } from '@/config/appConfig';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Custom hook to fetch current user's username
const useCurrentUserUsername = () => {
    const { user } = useAuth();
    const { db, firestoreFuncs, isLoading: isFirestoreLoading } = useFirestore();
    const [username, setUsername] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (user && db && firestoreFuncs && !isFirestoreLoading) {
            setIsLoading(true);
            const userDocRef = firestoreFuncs.doc(db, 'users', user.uid);
            const unsubscribe = firestoreFuncs.onSnapshot(
                userDocRef,
                (docSnap) => {
                    if (docSnap.exists()) {
                        const userData = docSnap.data() as UserFirestoreData;
                        setUsername(userData.username || null);
                    } else {
                        setUsername(null);
                    }
                    setIsLoading(false);
                },
                (error) => {
                    console.error('Error fetching user username:', error);
                    setUsername(null);
                    setIsLoading(false);
                }
            );
            return () => unsubscribe();
        } else {
            setUsername(null);
            setIsLoading(false);
        }
    }, [user, db, firestoreFuncs, isFirestoreLoading]);

    return { username, isLoading };
};

// Define UserSearchResult interface (from Task 58)
interface UserSearchResult {
    uid: string;
    username: string;
    displayName: string | null;
    photoURL: string | null;
}

// Define CigarSearchResult interface
interface CigarSearchResult {
    postId: string;
    cigarName: string;
    brand: string | null;
    mainImageUrl: string | null;
    username: string;
}

// Dynamically import LanguageSwitcher
const DynamicLanguageSwitcher = dynamic(
    () =>
        import('@/components/client/shared/LanguageSwitcher').then(
            (mod) => mod.LanguageSwitcher
        ),
    {
        loading: () => (
            <div className="h-8 w-8 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
            </div>
        ),
        ssr: false, // Language switcher is client-side interactive
    }
);

const DynamicThemeSwitcher = dynamic(
    () =>
        import('@/components/client/shared/ThemeSwitcher').then(
            (mod) => mod.ThemeSwitcher
        ),
    {
        loading: () => (
            <div className="h-8 w-8 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
            </div>
        ),
        ssr: false, // Theme switcher is client-side interactive
    }
);

const DynamicSheetContent = dynamic(
    () => import('@/components/ui/sheet').then((mod) => mod.SheetContent),
    {
        loading: () => (
            <div className="h-full w-full flex items-center justify-center p-4">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        ),
        ssr: false, // SheetContent is interactive and client-side
    }
);

// Helper function to get initials
const getInitials = (name?: string | null, email?: string | null): string => {
    if (name) {
        const parts = name.split(' ');
        if (parts.length > 1 && parts[0] && parts[parts.length - 1]) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }
    if (email) return email.substring(0, 2).toUpperCase();
    return '??';
};

// Main Navbar component definition (before memo)
const NavbarComponent: React.FC = () => {
    const {
        t,
        isLoading: isTranslationLoading,
        currentLanguage,
    } = useTranslation();
    const { user, isLoading: isAuthLoading, authInstance } = useAuth();
    const { username: currentUserUsername, isLoading: isUsernameLoading } = useCurrentUserUsername();
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const router = useRouter();
    const pathname = usePathname();

    // Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [userSearchResults, setUserSearchResults] = useState<
        UserSearchResult[]
    >([]); // Renamed for clarity
    const [isSearchingUsers, setIsSearchingUsers] = useState(false); // Specific loading state
    const [userSearchError, setUserSearchError] = useState<string | null>(null); // Specific error state

    const [searchType, setSearchType] = useState<'users' | 'cigars'>('users');
    const [cigarSearchResults, setCigarSearchResults] = useState<
        CigarSearchResult[]
    >([]);
    const [isSearchingCigars, setIsSearchingCigars] = useState(false); // Specific loading state
    const [cigarSearchError, setCigarSearchError] = useState<string | null>(
        null
    ); // Specific error state

    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    // Debounce search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300); // 300ms debounce delay

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    // Perform search when debounced query changes or searchType changes
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedSearchQuery.trim().length < 2) {
                setUserSearchResults([]);
                setCigarSearchResults([]);
                setUserSearchError(null);
                setCigarSearchError(null);
                return;
            }

            try {
                if (searchType === 'users') {
                    setIsSearchingUsers(true);
                    setUserSearchError(null);
                    setCigarSearchResults([]); // Clear other results
                    const response = await fetch(
                        `/api/users/search?q=${encodeURIComponent(debouncedSearchQuery.trim())}`
                    );
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(
                            errorData.error ||
                                t('navbar.search.error.genericUser', {
                                    fallback: 'User search failed',
                                })
                        );
                    }
                    const data = await response.json();
                    setUserSearchResults(data.users || []);
                    if (data.users.length === 0) {
                        setUserSearchError(
                            t('navbar.search.error.noUserResults', {
                                fallback: 'No users found.',
                            })
                        );
                    }
                } else {
                    // searchType === 'cigars'
                    setIsSearchingCigars(true);
                    setCigarSearchError(null);
                    setUserSearchResults([]); // Clear other results
                    const response = await fetch(
                        `/api/posts/search?q=${encodeURIComponent(debouncedSearchQuery.trim())}`
                    );
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(
                            errorData.error ||
                                t('navbar.search.error.genericCigar', {
                                    fallback: 'Cigar search failed',
                                })
                        );
                    }
                    const data = await response.json();
                    setCigarSearchResults(data.cigars || []);
                    if (data.cigars.length === 0) {
                        setCigarSearchError(
                            t('navbar.search.error.noCigarResults', {
                                fallback: 'No cigars found.',
                            })
                        );
                    }
                }
            } catch (err) {
                const message =
                    err instanceof Error
                        ? err.message
                        : t('navbar.search.error.generic', {
                              fallback: 'Search failed',
                          });
                if (searchType === 'users') {
                    setUserSearchError(message);
                    setUserSearchResults([]);
                } else {
                    setCigarSearchError(message);
                    setCigarSearchResults([]);
                }
                toast.error(
                    t('navbar.search.error.title', {
                        fallback: 'Search Error',
                    }),
                    { description: message }
                );
            } finally {
                if (searchType === 'users') setIsSearchingUsers(false);
                else setIsSearchingCigars(false);
            }
        };

        if (isSearchOpen) {
            performSearch();
        }
    }, [debouncedSearchQuery, isSearchOpen, searchType, t]);

    // Close search on route change
    useEffect(() => {
        if (isSearchOpen) {
            setIsSearchOpen(false);
            setSearchQuery('');
            setDebouncedSearchQuery('');
            setUserSearchResults([]);
            setCigarSearchResults([]);
            setUserSearchError(null);
            setCigarSearchError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Focus input when search opens
    useEffect(() => {
        if (isSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isSearchOpen]);

    // Close search when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target as Node)
            ) {
                setIsSearchOpen(false);
            }
        };

        if (isSearchOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSearchOpen]);

    const handleLogout = async () => {
        if (!authInstance) {
            console.error('Auth instance not available for logout.');
            toast.error(
                t('common.actions.logoutError', {
                    fallback: 'Logout failed: Auth service unavailable',
                })
            );
            return;
        }
        try {
            await signOut(authInstance); // Use the resolved authInstance and SDK signOut
            router.push(`/${currentLanguage}/login`);
            toast.success(
                t('common.actions.logout', {
                    fallback: 'Logged out successfully',
                })
            );
            closeSheet(); // Close sheet on logout
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error signing out:', error);
            }
            toast.error(
                t('common.actions.logoutError', {
                    fallback: 'Failed to log out',
                })
            );
        }
    };

    // Close sheet helper
    const closeSheet = () => setIsSheetOpen(false);

    // Determine user status for link visibility
    const getUserStatus = (): 'loggedIn' | 'loggedOut' | 'anonOnly' => {
        if (!user) return 'loggedOut';
        if (user.isAnonymous) return 'anonOnly';
        return 'loggedIn';
    };
    const userStatus = getUserStatus();

    // Filter navigation items based on user status
    const filterNavItems = (items: NavItemConfig[]) => {
        return items.filter((item) => {
            switch (item.visibility) {
                case 'always':
                    return true;
                case 'loggedIn':
                    return userStatus === 'loggedIn';
                case 'loggedOut':
                    return userStatus === 'loggedOut';
                case 'anonOnly':
                    return userStatus === 'anonOnly';
                default:
                    return false;
            }
        });
    };
    const visibleNavItems = filterNavItems(navigationConfig);

    // Render Nav Items for Desktop or Mobile Sheet
    const renderNavItems = (isMobile = false) => {
        const linkClass = isMobile
            ? 'flex items-center gap-3 rounded-lg px-3 py-2 text-foreground transition-all hover:text-primary text-base' // Mobile link styling (updated text color)
            : 'text-sm font-medium text-foreground transition-colors hover:text-primary'; // Desktop link styling (updated text color)
        const buttonVariant = 'ghost'; // Use ghost for both now for consistency, remove underline
        const buttonSize = isMobile ? 'default' : 'sm';
        const buttonClass = cn(
            isMobile
                ? 'justify-start w-full'
                : 'p-0 h-auto hover:bg-transparent hover:text-primary', // Desktop: no padding, transparent hover bg
            'text-sm' // Keep consistent font size
        );

        return (
            <nav
                className={cn(
                    isMobile
                        ? 'grid gap-2 font-medium'
                        : 'hidden md:flex md:flex-row md:items-center md:gap-5 lg:gap-6' // No RTL needed here
                )}
            >
                {/* Logo link for mobile */}
                {isMobile && (
                    <Link
                        href={`/${currentLanguage}/`}
                        className="flex items-center gap-2 text-lg font-semibold mb-4"
                        onClick={closeSheet}
                    >
                        <span className="sr-only">
                            {t(appConfig.appNameKey)}
                        </span>
                    </Link>
                )}
                {/* Regular nav items */}
                {visibleNavItems.map((item) => (
                    <Button
                        key={item.href}
                        variant={buttonVariant}
                        size={buttonSize}
                        className={buttonClass}
                        asChild
                    >
                        <Link
                            href={
                                item.isExternal
                                    ? item.href
                                    : `/${currentLanguage}${item.href}`
                            }
                            onClick={isMobile ? closeSheet : undefined}
                            className={linkClass}
                            target={item.isExternal ? '_blank' : undefined}
                            rel={
                                item.isExternal
                                    ? 'noopener noreferrer'
                                    : undefined
                            }
                        >
                            {isMobile && <item.icon className="h-5 w-5" />}
                            {t(item.labelKey)}
                        </Link>
                    </Button>
                ))}
                {/* Auth actions for mobile */}
                {isMobile && (
                    <>
                        <hr className="my-4" />
                        {userStatus === 'loggedIn' && (
                            <Button
                                variant="ghost"
                                onClick={handleLogout}
                                className="justify-start w-full flex items-center gap-3 rounded-lg px-3 py-2 text-foreground transition-all hover:text-primary text-base" // Updated text color
                            >
                                <LogOut className="h-5 w-5" />
                                {t('common.logout')}
                            </Button>
                        )}
                        {userStatus === 'loggedOut' && (
                            <>
                                <Button
                                    variant="ghost"
                                    asChild
                                    className="justify-start w-full flex items-center gap-3 rounded-lg px-3 py-2 text-foreground transition-all hover:text-primary text-base" // Updated text color
                                >
                                    <Link
                                        href={`/${currentLanguage}/login`}
                                        onClick={closeSheet}
                                    >
                                        <LogIn className="h-5 w-5" />
                                        {t('common.login')}
                                    </Link>
                                </Button>
                                <Button
                                    variant="ghost"
                                    asChild
                                    className="justify-start w-full flex items-center gap-3 rounded-lg px-3 py-2 text-foreground transition-all hover:text-primary text-base" // Updated text color
                                >
                                    <Link
                                        href={`/${currentLanguage}/signup`}
                                        onClick={closeSheet}
                                    >
                                        <UserPlus className="h-5 w-5" />
                                        {t('common.signup')}
                                    </Link>
                                </Button>
                            </>
                        )}
                    </>
                )}
            </nav>
        );
    };

    // Loading Skeleton
    if (isTranslationLoading || isAuthLoading || !authInstance || (user && isUsernameLoading)) {
        return (
            <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
                <div className="container mx-auto flex h-14 max-w-screen-2xl items-center justify-between px-4 md:px-6">
                    {/* Simplified Skeleton */}
                    <Skeleton className="h-6 w-24" />
                    <div className="flex items-center gap-x-2">
                        <Skeleton className="h-8 w-8 rounded-md" />
                        <Skeleton className="h-8 w-8 rounded-md" />
                        <Skeleton className="h-8 w-8 rounded-full" />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
            <div className="container mx-auto flex h-14 max-w-screen-2xl items-center justify-between px-4 md:px-6">
                {/* Left Side: Logo and Desktop Nav */}
                <div className="flex items-center gap-6">
                    <Link
                        href={`/${currentLanguage}/`}
                        className="flex items-center gap-2 rtl:space-x-reverse"
                    >
                        <span className="font-bold bg-gradient-to-r from-primary to-tertiary bg-clip-text text-transparent">
                            {t(appConfig.appNameKey)}
                        </span>
                    </Link>
                    {renderNavItems(false)} {/* Desktop Nav */}
                </div>

                {/* Right Side: Language, Theme, Auth/User Menu */}
                <div className="flex items-center gap-1 sm:gap-2">
                    <DynamicLanguageSwitcher />
                    <DynamicThemeSwitcher />

                    {/* Search Area */}
                    <div
                        ref={searchContainerRef}
                        className="relative flex items-center"
                    >
                        {!isSearchOpen && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setIsSearchOpen(true)}
                            >
                                <Search className="h-4 w-4" />
                                <span className="sr-only">
                                    {t('navbar.search.toggle', {
                                        fallback: 'Toggle search',
                                    })}
                                </span>
                            </Button>
                        )}
                        {isSearchOpen && (
                            <div className="flex items-center gap-1">
                                <Input
                                    ref={searchInputRef}
                                    type="search"
                                    placeholder={
                                        searchType === 'users'
                                            ? t(
                                                  'navbar.search.placeholderUser',
                                                  {
                                                      fallback:
                                                          'Search users...',
                                                  }
                                              )
                                            : t(
                                                  'navbar.search.placeholderCigar',
                                                  {
                                                      fallback:
                                                          'Search cigars...',
                                                  }
                                              )
                                    }
                                    className="h-8 w-32 sm:w-48 md:w-56 text-sm"
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => {
                                        setIsSearchOpen(false);
                                        setSearchQuery('');
                                        setDebouncedSearchQuery('');
                                        setUserSearchResults([]);
                                        setCigarSearchResults([]);
                                        setUserSearchError(null);
                                        setCigarSearchError(null);
                                    }}
                                >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">
                                        {t('navbar.search.close', {
                                            fallback: 'Close search',
                                        })}
                                    </span>
                                </Button>
                            </div>
                        )}
                        {/* Search Results Dropdown */}
                        {isSearchOpen &&
                            (searchQuery.length >= 2 ||
                                userSearchResults.length > 0 ||
                                cigarSearchResults.length > 0 ||
                                isSearchingUsers ||
                                isSearchingCigars ||
                                userSearchError ||
                                cigarSearchError) && (
                                <div className="absolute top-full mt-1 w-72 sm:w-80 md:w-96 rounded-md border bg-popover p-2 shadow-md z-50 max-h-96 overflow-y-auto">
                                    <Tabs
                                        value={searchType}
                                        onValueChange={(value) => {
                                            setSearchType(
                                                value as 'users' | 'cigars'
                                            );
                                            setUserSearchResults([]);
                                            setCigarSearchResults([]);
                                            setUserSearchError(null);
                                            setCigarSearchError(null);
                                        }}
                                        className="w-full"
                                    >
                                        <TabsList className="grid w-full grid-cols-2 mb-2 h-8">
                                            <TabsTrigger
                                                value="users"
                                                className="text-xs h-full"
                                            >
                                                {t('navbar.search.typeUsers', {
                                                    fallback: 'Users',
                                                })}
                                            </TabsTrigger>
                                            <TabsTrigger
                                                value="cigars"
                                                className="text-xs h-full"
                                            >
                                                {t('navbar.search.typeCigars', {
                                                    fallback: 'Cigars',
                                                })}
                                            </TabsTrigger>
                                        </TabsList>
                                        <TabsContent value="users">
                                            {isSearchingUsers && (
                                                <div className="flex items-center justify-center p-3">
                                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                                    <span className="ml-2 text-sm text-muted-foreground">
                                                        {t(
                                                            'navbar.search.searchingUsers',
                                                            {
                                                                fallback:
                                                                    'Searching users...',
                                                            }
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {!isSearchingUsers &&
                                                userSearchError && (
                                                    <div className="p-3 text-sm text-destructive flex items-center">
                                                        <AlertCircle className="h-4 w-4 mr-2" />
                                                        {userSearchError}
                                                    </div>
                                                )}
                                            {!isSearchingUsers &&
                                                !userSearchError &&
                                                userSearchResults.length >
                                                    0 && (
                                                    <ul className="space-y-1">
                                                        {userSearchResults.map(
                                                            (resultUser) => (
                                                                <li
                                                                    key={
                                                                        resultUser.uid
                                                                    }
                                                                >
                                                                    <Link
                                                                        href={`/${currentLanguage}/profile/${resultUser.username}`}
                                                                        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors"
                                                                        onClick={() =>
                                                                            setIsSearchOpen(
                                                                                false
                                                                            )
                                                                        }
                                                                    >
                                                                        <Avatar className="h-7 w-7 text-xs">
                                                                            <AvatarImage
                                                                                src={
                                                                                    resultUser.photoURL ||
                                                                                    undefined
                                                                                }
                                                                                alt={
                                                                                    resultUser.displayName ||
                                                                                    resultUser.username
                                                                                }
                                                                            />
                                                                            <AvatarFallback>
                                                                                {getInitials(
                                                                                    resultUser.displayName,
                                                                                    resultUser.username
                                                                                )}
                                                                            </AvatarFallback>
                                                                        </Avatar>
                                                                        <div className="flex-1 truncate">
                                                                            <p className="text-sm font-medium text-popover-foreground truncate">
                                                                                {resultUser.displayName ||
                                                                                    resultUser.username}
                                                                            </p>
                                                                            <p className="text-xs text-muted-foreground truncate">
                                                                                @
                                                                                {
                                                                                    resultUser.username
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                    </Link>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                )}
                                            {!isSearchingUsers &&
                                                !userSearchError &&
                                                userSearchResults.length ===
                                                    0 &&
                                                debouncedSearchQuery.length >=
                                                    2 && (
                                                    <div className="p-3 text-sm text-muted-foreground text-center">
                                                        {t(
                                                            'navbar.search.error.noUserResults',
                                                            {
                                                                fallback:
                                                                    'No users found.',
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                        </TabsContent>
                                        <TabsContent value="cigars">
                                            {isSearchingCigars && (
                                                <div className="flex items-center justify-center p-3">
                                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                                    <span className="ml-2 text-sm text-muted-foreground">
                                                        {t(
                                                            'navbar.search.searchingCigars',
                                                            {
                                                                fallback:
                                                                    'Searching cigars...',
                                                            }
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {!isSearchingCigars &&
                                                cigarSearchError && (
                                                    <div className="p-3 text-sm text-destructive flex items-center">
                                                        <AlertCircle className="h-4 w-4 mr-2" />
                                                        {cigarSearchError}
                                                    </div>
                                                )}
                                            {!isSearchingCigars &&
                                                !cigarSearchError &&
                                                cigarSearchResults.length >
                                                    0 && (
                                                    <ul className="space-y-1">
                                                        {cigarSearchResults.map(
                                                            (cigar) => (
                                                                <li
                                                                    key={
                                                                        cigar.postId
                                                                    }
                                                                >
                                                                    <Link
                                                                        href={`/${currentLanguage}/post/${cigar.postId}`}
                                                                        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors"
                                                                        onClick={() =>
                                                                            setIsSearchOpen(
                                                                                false
                                                                            )
                                                                        }
                                                                    >
                                                                        <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center text-xs font-medium overflow-hidden flex-shrink-0">
                                                                            {cigar.mainImageUrl ? (
                                                                                <img
                                                                                    src={
                                                                                        cigar.mainImageUrl
                                                                                    }
                                                                                    alt={
                                                                                        cigar.cigarName
                                                                                    }
                                                                                    className="h-full w-full object-cover"
                                                                                />
                                                                            ) : (
                                                                                <ArchiveX className="h-5 w-5 text-muted-foreground" />
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 truncate">
                                                                            <p className="text-sm font-medium text-popover-foreground truncate">
                                                                                {
                                                                                    cigar.cigarName
                                                                                }
                                                                            </p>
                                                                            <p className="text-xs text-muted-foreground truncate">
                                                                                {cigar.brand &&
                                                                                    `${cigar.brand} • `}
                                                                                by
                                                                                @
                                                                                {
                                                                                    cigar.username
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                    </Link>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                )}
                                            {!isSearchingCigars &&
                                                !cigarSearchError &&
                                                cigarSearchResults.length ===
                                                    0 &&
                                                debouncedSearchQuery.length >=
                                                    2 && (
                                                    <div className="p-3 text-sm text-muted-foreground text-center">
                                                        {t(
                                                            'navbar.search.error.noCigarResults',
                                                            {
                                                                fallback:
                                                                    'No cigars found.',
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                        </TabsContent>
                                    </Tabs>
                                </div>
                            )}
                    </div>

                    {/* Conditional Auth Buttons or User Dropdown */}
                    {userStatus === 'loggedIn' ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="relative h-8 w-8 rounded-full"
                                >
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage
                                            src={user?.photoURL || undefined}
                                            alt={
                                                user?.displayName ||
                                                user?.email ||
                                                'User'
                                            }
                                        />
                                        <AvatarFallback>
                                            {getInitials(
                                                user?.displayName,
                                                user?.email
                                            )}
                                        </AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                className="w-56"
                                align="end"
                                forceMount
                            >
                                <DropdownMenuLabel className="font-normal">
                                    <div className="flex flex-col gap-1">
                                        <p className="text-sm font-medium leading-none">
                                            {user?.displayName ||
                                                t('navbar.userAccount')}
                                        </p>
                                        <p className="text-xs leading-none text-muted-foreground">
                                            {user?.email}
                                        </p>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                    <Link
                                        href={currentUserUsername ? `/${currentLanguage}/profile/${currentUserUsername}` : `/${currentLanguage}/profile`}
                                        className="flex items-center"
                                    >
                                        <UserIcon className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
                                        <span>
                                            {t('navbar.profile', {
                                                fallback: 'Profile',
                                            })}
                                        </span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link
                                        href={`/${currentLanguage}/settings/account`}
                                        className="flex items-center"
                                    >
                                        <Settings className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
                                        <span>{t('navbar.settings')}</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout}>
                                    <LogOut className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
                                    <span>{t('common.logout')}</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="flex items-center gap-1">
                            <Button
                                variant="outline"
                                size="sm"
                                className="hidden sm:flex"
                                asChild
                            >
                                <Link href={`/${currentLanguage}/login`}>
                                    {t('common.login')}
                                </Link>
                            </Button>
                            <Button
                                size="sm"
                                className="hidden sm:flex"
                                asChild
                            >
                                <Link href={`/${currentLanguage}/signup`}>
                                    {t('common.signup')}
                                </Link>
                            </Button>
                        </div>
                    )}

                    {/* Mobile Navigation Sheet */}
                    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden h-8 w-8"
                            >
                                <Menu className="h-5 w-5" />
                                <span className="sr-only">
                                    {t('navbar.toggleMenu')}
                                </span>
                            </Button>
                        </SheetTrigger>
                        <DynamicSheetContent side="right" className="w-72">
                            <div className="flex flex-col h-full">
                                <div className="flex-1">
                                    {renderNavItems(true)} {/* Mobile Nav */}
                                </div>
                            </div>
                        </DynamicSheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

// Memoize the component for performance
const Navbar = memo(NavbarComponent);
Navbar.displayName = 'Navbar';

export default Navbar;
